import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MaterialPictureCollection } from '@core/models/games/pictures.model';
import { AuthService } from '@core/services';
import { PicturesService } from '@core/services/games';
import { MethodicMaterialsFiltersManager } from '@games/core/lib/methodic-materials/filters';
import { FilterPictureParameters } from '@games/speed-reading/filter-parameters';
import { Language, methodicMaterialsLanguages } from '@new-core/types/methodic-materials.types';
import { TranslateService } from '@ngx-translate/core';
import { AccessAccountType } from '@shared/components/games/models/model';
import { Item } from '@shared/components/multiselect-with-search/multiselect-with-search.component';
import { Observable, Subscription } from 'rxjs';

@Component({
  selector: 'ms-pictures-filter',
  templateUrl: './pictures-filter.component.html',
  styleUrls: ['./pictures-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PicturesFilterComponent implements AfterViewInit, OnDestroy, OnChanges, OnInit {
  public langs = [{ title: 'materials.langs.any', value: 'any' }, ...methodicMaterialsLanguages];

  /**
   * Элементы для компонента выбора.
   */
  public itemsForSelectComponent: Item[] = [];

  filterVars = ['all', 'mine', 'others', 'official', 'my_center'];

  private _data: MaterialPictureCollection[] = [];

  $picturesStream: Subscription;

  isLoading = false;

  private _accessAccountType: AccessAccountType[] = [
    'teacher',
    'admin',
    'manager',
    'partner_manager',
    'partner_admin',
  ];

  get isAccessAccountType(): boolean {
    const accountType: any = this._authService.accountType;

    return this._accessAccountType.includes(accountType);
  }

  public get data(): MaterialPictureCollection[] {
    return this._data;
  }

  public set data(value: MaterialPictureCollection[]) {
    this._data = value;
    this.itemsForSelectComponent = value.map((item) => ({
      title: item.title,
      value: item.pk,
    }));
    this.isLoading = false;
    this._cdr.detectChanges();
  }

  public get isDataEmpty(): boolean {
    const isEmpty = this.data.length === 0;
    this.hasError.emit(isEmpty);
    return isEmpty;
  }

  public get placeholderTranslationKeyForSelectComponent(): string {
    return this.selectedIds.length > 0 ? '' : 'textsFilter.randomPicture';
  }

  public get selectedIds(): number[] {
    const value = this.parameters.filterPictureId;
    return Array.isArray(value) ? value : [];
  }

  // @Input('form') parametersForm: FormGroup;

  /**
   * Произвольное минимальное количество методических материалов.
   *
   * Передавать этот параметр необязательно, позволяет некоторым играм
   * сообщить свои требования к размеру коллекций материалов.
   */
  @Input() customMinItems?: number;

  /**
   * Уведомить хост об ошибке в форме фильтров.
   */
  @Output() hasError = new EventEmitter<boolean>();

  get parameters(): FilterPictureParameters {
    return this._parameters.value;
  }

  get _parameters() {
    return this.methodicMaterials._parameters;
  }

  readonly methodicMaterials = inject(MethodicMaterialsFiltersManager);

  constructor(
    private _picturesService: PicturesService,
    private _authService: AuthService,
    private _cdr: ChangeDetectorRef,
    private _translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    this._setCurrentLanguageAsFilter();
  }

  ngAfterViewInit() {
    this.$picturesStream = this.methodicMaterials
      .updateMaterialsOnParametersFormChange(() => this._getMaterials())
      .subscribe({
        next: (materials) => {
          this.data = materials as MaterialPictureCollection[];
        },
      });
  }

  ngOnDestroy() {
    if (this.$picturesStream) {
      this.$picturesStream.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('customMinItems' in changes) {
      this._getMaterials().subscribe((materials) => {
        this.data = materials as MaterialPictureCollection[];
      });
    }
  }

  public updateSelectedIds(newSelectedIds: number[]) {
    this._parameters
      .get('filterPictureId')
      .setValue(Array.isArray(newSelectedIds) ? newSelectedIds : []);
  }

  private _getMaterials(): Observable<MaterialPictureCollection[]> {
    this.isLoading = true;

    let filters = this.methodicMaterials.filtersForPictures;
    if (this.customMinItems != undefined) {
      filters.minItems = this.customMinItems;
    }

    return this._picturesService.getMaterialsShortList(filters) as Observable<
      MaterialPictureCollection[]
    >;
  }

  /**
   * Установить фильтр по языку равным текущему языку локализации платформы.
   */
  private _setCurrentLanguageAsFilter(): void {
    const currentLanguage = this._translateService.currentLang as Language;
    this._parameters.get('filterPLang').setValue(currentLanguage);
  }
}
